import { useCallback } from 'react';
import { TourStep } from './usePageTour';
import { usePageTour } from './usePageTour';
import { Grid } from 'antd';
import { useTranslation } from 'react-i18next';

const { useBreakpoint } = Grid;

export interface CRUDTourConfig {
  pageName: string;
  recordType: string; // e.g., 'block', 'apartment unit', 'resident'
  hasRecords: boolean;
  recordCount: number;
  firstRecordId?: string;
  additionalSteps?: TourStep[];
  onAddRecord?: () => void;
  onViewRecord?: (recordId: string) => void;
  onEditRecord?: (recordId: string) => void;
  onDeleteRecord?: (recordId: string) => void;
}

export const useCRUDTour = () => {
  const { startTour, stopTour, cleanup, isActive } = usePageTour();
  const screens = useBreakpoint();
  const { t } = useTranslation('common');

  // Helper function to get the active tab's data table selector
  const getActiveTabDataTableSelector = () => {
    const activeTabContent = document.querySelector('.ant-tabs-content-holder .ant-tabs-tabpane-active');
    if (activeTabContent) {
      const dataTable = activeTabContent.querySelector('.data-table-card, .data-table');
      if (dataTable) {
        return '.ant-tabs-content-holder .ant-tabs-tabpane-active .data-table-card, .ant-tabs-content-holder .ant-tabs-tabpane-active .data-table';
      }
    }
    return '.data-table-card, .data-table';
  };

  // Helper function to get the active tab's search input selector
  const getActiveTabSearchSelector = () => {
    const activeTabContent = document.querySelector('.ant-tabs-content-holder .ant-tabs-tabpane-active');
    if (activeTabContent) {
      const searchInput = activeTabContent.querySelector('.table-actions .search-input, .table-actions input[placeholder*="search"], .table-actions input[placeholder*="Search"]');
      if (searchInput) {
        return '.ant-tabs-content-holder .ant-tabs-tabpane-active .table-actions .search-input, .ant-tabs-content-holder .ant-tabs-tabpane-active .table-actions input[placeholder*="search"], .ant-tabs-content-holder .ant-tabs-tabpane-active .table-actions input[placeholder*="Search"]';
      }
    }
    return '.table-actions .search-input, .table-actions input[placeholder*="search"], .table-actions input[placeholder*="Search"]';
  };

  // Helper function to get the active tab's add button selector
  const getActiveTabAddButtonSelector = () => {
    const activeTabContent = document.querySelector('.ant-tabs-content-holder .ant-tabs-tabpane-active');
    if (activeTabContent) {
      const addButton = activeTabContent.querySelector('.table-actions .add-record-button.ant-btn');
      if (addButton) {
        return '.ant-tabs-content-holder .ant-tabs-tabpane-active .table-actions .add-record-button.ant-btn';
      }
    }
    return '.table-actions .add-record-button.ant-btn';
  };

  // Helper function to get the active tab's record actions selector
  const getActiveTabRecordActionsSelector = () => {
    const activeTabContent = document.querySelector('.ant-tabs-content-holder .ant-tabs-tabpane-active');
    if (activeTabContent) {
      const recordActions = activeTabContent.querySelector('.record-actions');
      if (recordActions) {
        return '.ant-tabs-content-holder .ant-tabs-tabpane-active .record-actions';
      }
    }
    return '.record-actions';
  };

  // Helper function to get the active tab's pagination selector
  const getActiveTabPaginationSelector = () => {
    const activeTabContent = document.querySelector('.ant-tabs-content-holder .ant-tabs-tabpane-active');
    if (activeTabContent) {
      const pagination = activeTabContent.querySelector('.pagination-section .ant-pagination, .ant-pagination');
      if (pagination) {
        return '.ant-tabs-content-holder .ant-tabs-tabpane-active .pagination-section .ant-pagination, .ant-tabs-content-holder .ant-tabs-tabpane-active .ant-pagination';
      }
    }
    return '.pagination-section .ant-pagination, .ant-pagination';
  };

  // Helper function to get the active tab's mobile list selector
  const getActiveTabMobileListSelector = () => {
    const activeTabContent = document.querySelector('.ant-tabs-content-holder .ant-tabs-tabpane-active');
    if (activeTabContent) {
      const list = activeTabContent.querySelector('.ant-list:has(.ant-card)');
      if (list) {
        return '.ant-tabs-content-holder .ant-tabs-tabpane-active .ant-list:has(.ant-card)';
      }
    }
    return '.ant-list:has(.ant-card)';
  };

  // Helper function to get the active tab's mobile search input selector
  const getActiveTabMobileSearchSelector = (recordType: string) => {
    const searchInputId = recordType === 'apartment unit' ? 'units' : `${recordType}s`;
    return `#${searchInputId}-search-input`;
  };

  // Helper function to get the active tab's mobile add button selector
  const getActiveTabMobileAddButtonSelector = (recordType: string) => {
    const addButtonId = recordType === 'apartment unit' ? 'units' : `${recordType}s`;
    return `#${addButtonId}-add-button`;
  };

  // Helper function to get the active tab's mobile card actions selector
  const getActiveTabMobileCardActionsSelector = () => {
    const activeTabContent = document.querySelector('.ant-tabs-content-holder .ant-tabs-tabpane-active');
    if (activeTabContent) {
      const cardActions = activeTabContent.querySelector('.ant-card-actions');
      if (cardActions) {
        return '.ant-tabs-content-holder .ant-tabs-tabpane-active .ant-card-actions';
      }
    }
    return '.ant-card-actions';
  };

  const generateCRUDSteps = useCallback((config: CRUDTourConfig, isMobile: boolean): TourStep[] => {
    const steps: TourStep[] = [];
    const { recordType, hasRecords, recordCount, additionalSteps } = config;
    
    // Create plural form for translations
    const getRecordTypePlural = (type: string) => {
      switch (type) {
        case 'block':
          return 'blocks';
        case 'apartment unit':
          return 'apartment units';
        case 'parking':
          return 'parkings';
        case 'amenity':
          return 'amenities';
        default:
          return `${type}s`;
      }
    };
    
    const recordTypePlural = getRecordTypePlural(recordType);
    

    // Step 1: Category Tabs (always first - most important navigation)
    steps.push({
      element: '.properties-tabs .ant-tabs-nav',
      popover: {
        title: t('tour.categoryNavigation'),
        description: t('tour.categoryNavigationDescription'),
        side: 'bottom'
      }
    });

    // Step 2: Data Table (always show - with or without records)
    if (isMobile) {
      // Mobile: Use List component - target the active tab's list container
      const activeTabMobileListSelector = getActiveTabMobileListSelector();
      steps.push({
        element: activeTabMobileListSelector,
        popover: {
          title: t('tour.dataTable', { recordTypePlural }),
          description: hasRecords 
            ? t('tour.dataTableDescription', { recordTypePlural, count: recordCount })
            : t('tour.emptyStateDescription', { recordTypePlural }),
          side: 'top'
        }
      });
      
    } else {
      // Desktop: Use DataTable component - target the active tab's data table
      const activeTabSelector = getActiveTabDataTableSelector();
      steps.push({
        element: activeTabSelector,
        popover: {
          title: t('tour.dataTable', { recordTypePlural }),
          description: hasRecords 
            ? t('tour.dataTableDescription', { recordTypePlural, count: recordCount })
            : t('tour.emptyStateDescription', { recordTypePlural }),
          side: 'top'
        }
      });
    }

    // Step 3: Bulk Operations (only if records exist, not on mobile, and page has bulk operations)
    if (hasRecords && recordCount > 0 && !isMobile) {
      // Only show bulk operations for pages that have them implemented
      // Currently only Blocks page has bulk operations in the UI
      const hasBulkOperations = config.recordType === 'block';
      
      if (hasBulkOperations) {
        steps.push({
          element: '.bulk-delete-button, .bulk-upload-button',
          popover: {
            title: t('tour.bulkOperations'),
            description: t('tour.bulkOperationsDescription'),
            side: 'top'
          }
        });
      }
    }

    // Step 3/4: Search Input (responsive)
    if (isMobile) {
      // Mobile: Search input is in List header - target active tab
      const activeTabMobileSearchSelector = getActiveTabMobileSearchSelector(recordType);
      steps.push({
        element: activeTabMobileSearchSelector,
        popover: {
          title: t('tour.searchRecords'),
          description: t('tour.searchRecordsDescription'),
          side: 'bottom'
        }
      });
    } else {
      // Desktop: Search input is in DataTable table-actions row (headerActions) - target active tab
      const activeTabSearchSelector = getActiveTabSearchSelector();
      steps.push({
        element: activeTabSearchSelector,
        popover: {
          title: t('tour.searchRecords'),
          description: t('tour.searchRecordsDescription'),
          side: 'bottom'
        }
      });
    }

    // Step 4/5: Add Record Button (responsive)
    if (isMobile) {
      // Mobile: Add button is in List header - target active tab
      const activeTabMobileAddButtonSelector = getActiveTabMobileAddButtonSelector(recordType);
      steps.push({
        element: activeTabMobileAddButtonSelector,
        popover: {
          title: t('tour.addNewRecord'),
          description: t('tour.addNewRecordDescription', { recordType }),
          side: 'bottom'
        }
      });
    } else {
      // Desktop: Add button is in DataTable table-actions row (headerActions) - target active tab
      const activeTabAddButtonSelector = getActiveTabAddButtonSelector();
      steps.push({
        element: activeTabAddButtonSelector,
        popover: {
          title: t('tour.addNewRecord'),
          description: t('tour.addNewRecordDescription', { recordType }),
          side: 'bottom'
        }
      });
    }

    // Step 5/6: Record Actions (only if records exist)
    if (hasRecords && recordCount > 0) {
      if (isMobile) {
        // Mobile: Action buttons are in Card actions - target active tab
        const activeTabMobileCardActionsSelector = getActiveTabMobileCardActionsSelector();
        steps.push({
          element: activeTabMobileCardActionsSelector,
          popover: {
            title: t('tour.recordActions'),
            description: t('tour.recordActionsDescription', { recordType }),
            side: 'left'
          }
        });
      } else {
        // Desktop: Action buttons are in record-actions class - target active tab
        const activeTabRecordActionsSelector = getActiveTabRecordActionsSelector();
        steps.push({
          element: activeTabRecordActionsSelector,
          popover: {
            title: t('tour.recordActions'),
            description: t('tour.recordActionsDescription', { recordType }),
            side: 'left'
          }
        });
      }
    }

    // Step 6/7: Pagination (responsive - only if records exist)
    if (hasRecords && recordCount > 0) {
      if (isMobile) {
        // Mobile: Pagination is separate below List - target active tab
        const activeTabPaginationSelector = getActiveTabPaginationSelector();
        steps.push({
          element: activeTabPaginationSelector,
          popover: {
            title: t('tour.pagination'),
            description: t('tour.paginationDescription'),
            side: 'top'
          }
        });
      } else {
        // Desktop: Pagination is built into DataTable - target active tab
        const activeTabPaginationSelector = getActiveTabPaginationSelector();
        steps.push({
          element: activeTabPaginationSelector,
          popover: {
            title: t('tour.pagination'),
            description: t('tour.paginationDescription'),
            side: 'top'
          }
        });
      }
    }

    // Add page-specific additional steps
    if (additionalSteps) {
      steps.push(...additionalSteps);
    }

    return steps;
  }, [t]);

  const startCRUDTour = useCallback((config: CRUDTourConfig) => {
    // Determine screen size at tour start - force re-evaluation
    const isMobile = !screens.md;
    
    // Function to wait for elements to be ready with retry mechanism
    const waitForElements = (maxRetries = 20, delay = 400) => {
      return new Promise((resolve, reject) => {
        let retries = 0;
        
        const checkElements = () => {
          retries++;
          
          // Get page-specific selectors based on recordType
          const getPageSpecificSelectors = () => {
            const baseSelectors = ['.properties-tabs .ant-tabs-nav'];
            
            if (isMobile) {
              baseSelectors.push('.ant-list:has(.ant-card)');
              // Page-specific mobile selectors
              switch (config.recordType) {
                case 'block':
                  baseSelectors.push('#blocks-search-input', '#blocks-add-button');
                  break;
                case 'unit':
                  baseSelectors.push('#units-search-input', '#units-add-button');
                  break;
                case 'parking':
                  baseSelectors.push('#parkings-search-input', '#parkings-add-button');
                  break;
                case 'amenity':
                  baseSelectors.push('#amenities-search-input', '#amenities-add-button');
                  break;
              }
            } else {
              baseSelectors.push('.data-table-card, .data-table');
              baseSelectors.push('.table-actions .search-input', '.table-actions .add-record-button.ant-btn');
            }
            
            return baseSelectors;
          };
          
          const requiredSelectors = getPageSpecificSelectors();
          const missingElements = requiredSelectors.filter(selector => {
            const elements = document.querySelectorAll(selector);
            return elements.length === 0;
          });
          
          // Additional check: ensure the elements are actually visible and rendered
          const visibleElements = requiredSelectors.filter(selector => {
            const elements = document.querySelectorAll(selector);
            return elements.length > 0 && Array.from(elements).some(el => {
              const rect = el.getBoundingClientRect();
              return rect.width > 0 && rect.height > 0;
            });
          });
          
          if (missingElements.length === 0 && visibleElements.length === requiredSelectors.length) {
            resolve(true);
          } else if (retries >= maxRetries) {
            reject(new Error('Elements not found or not visible after max retries'));
          } else {
            setTimeout(checkElements, delay);
          }
        };
        
        // Start checking after a longer delay to ensure page is fully rendered
        setTimeout(checkElements, 200);
      });
    };
    
    // Start the tour after elements are ready
    waitForElements()
      .then(() => {
        // Generate steps fresh to ensure they match the current tab
        const steps = generateCRUDSteps(config, isMobile);
        startTour(steps, {
          onDestroyed: () => {
            // Tour completed
          }
        });
      })
      .catch(() => {
        // Try one more time with a longer delay
        setTimeout(() => {
          const steps = generateCRUDSteps(config, isMobile);
          startTour(steps, {
            onDestroyed: () => {
              // Tour completed (retry)
            }
          });
        }, 1000);
      });
  }, [generateCRUDSteps, startTour, screens]);

  return {
    startCRUDTour,
    stopTour,
    cleanup,
    isActive
  };
};
